import { parseWithZod } from "@conform-to/zod"
import { type ActionFunctionArgs, data } from "react-router"
import { z } from "zod"
import * as E from "@react-email/components"
import ContactForm, {
	type ContactFormSchema,
	contactFormResolver,
} from "#app/components/contact/contact-form"
import HandmadeQualityArts from "#app/components/logos/handmade-quality-arts"
import { APP_NAME } from "#app/utils/constants"
import { prisma } from "#app/utils/db.server"
import { generateMeta } from "#app/utils/meta.js"
import { redirectWithToast } from "#app/utils/toast.server"
import routes from "#routes"
import { sendEmail } from "#app/utils/email.server"
import sendSlackNotification from "#app/utils/slack.server.js"

export const meta = generateMeta({
	title: `Contacto - ${APP_NAME}`,
	description:
		"Contacta con Greta's Patchwork. Envíanos tus preguntas, comentarios o sugerencias. ¡Estamos aquí para ayudarte! Encuentra nuestros datos de contacto, formulario de contacto y horario de atención al cliente.",
	canonical: "/contact",
})

export async function action({ request }: ActionFunctionArgs) {
	const formData = await request.formData()
	const submission = parseWithZod(formData, {
		schema: contactFormResolver,
	})

	if (submission.status !== "success") {
		return data(
			{ errors: submission.reply().error, defaultValues: submission.payload },
			{ status: 400 },
		)
	}

	await prisma.contactRequest.create({
		data: submission.value,
	})

	sendSlackNotification(
		`Nueva solicitud de contacto de ${submission.value.name} (${submission.value.email}): ${submission.value.subject}`,
	)

	const adminEmail = process.env.ADMIN_EMAIL || "admin@gretaspatchwork.com"
	await sendEmail({
		to: adminEmail,
		subject: `Nueva solicitud de contacto - ${submission.value.subject}`,
		react: (
			<ContactRequestEmail
				name={submission.value.name}
				email={submission.value.email}
				subject={submission.value.subject}
				message={submission.value.message}
			/>
		),
	})

	return redirectWithToast(routes.public.base.route(), {
		title: "Mensaje recibido",
		description:
			"Hemos recibido tu mensaje con éxito, nos podremos en contacto contigo lo antes posible.",
	})
}

export default function ContactRoute() {
	return (
		<main className="container h-full">
			<div className="my-[50px] flex items-start justify-between gap-x-24">
				<ContactForm />

				<HandmadeQualityArts className="hidden max-w-lg lg:block" />
			</div>
		</main>
	)
}

function ContactRequestEmail({
	name,
	email,
	subject,
	message,
}: {
	name: string
	email: string
	subject: string
	message: string
}) {
	return (
		<E.Html lang="es" dir="ltr">
			<E.Container>
				<h1>
					<E.Text>Nueva solicitud de contacto</E.Text>
				</h1>
				<p>
					<E.Text>Se ha recibido una nueva solicitud de contacto:</E.Text>
				</p>
				<p>
					<E.Text>
						<strong>Nombre:</strong> {name}
					</E.Text>
				</p>
				<p>
					<E.Text>
						<strong>Email:</strong> {email}
					</E.Text>
				</p>
				<p>
					<E.Text>
						<strong>Asunto:</strong> {subject}
					</E.Text>
				</p>
				<p>
					<E.Text>
						<strong>Mensaje:</strong>
					</E.Text>
				</p>
				<p>
					<E.Text style={{ whiteSpace: "pre-wrap" }}>{message}</E.Text>
				</p>
				<E.Link
					href={`https://gretaspatchwork.com${routes.admin.contacts.route}`}
				>
					Ver en el panel de administración
				</E.Link>
			</E.Container>
		</E.Html>
	)
}
